
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const loginSchema = z.object({
  email: z.string()
    .email({ message: "Please enter a valid email address" })
    .trim(),
  password: z.string()
    .min(1, { message: "Password is required" }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const { signIn, user, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/app";

  useEffect(() => {
    if (user && session) {
      console.log("User already logged in, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [user, session, navigate, from]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    if (loading) return;
    
    if (loginAttempts >= 5) {
      toast.error("Too many login attempts", {
        description: "Please try again later or reset your password"
      });
      return;
    }
    
    setLoading(true);
    try {
      const { error } = await signIn(values.email, values.password);
      
      if (error) {
        console.error("Login error:", error);
        setLoginAttempts(prev => prev + 1);
        
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Login failed", {
            description: "Invalid email or password. Please check your credentials and try again."
          });
        } else if (error.message.includes("Email not confirmed")) {
          toast.error("Email not verified", {
            description: "Please check your email and verify your account before logging in."
          });
        } else if (error.message.includes("too many requests")) {
          toast.error("Login temporarily disabled", {
            description: "Too many login attempts. Please try again later."
          });
        } else {
          toast.error("Login failed", {
            description: error.message
          });
        }
      } else {
        toast.success("Welcome back!", {
          description: "You have successfully logged in."
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginAttempts(prev => prev + 1);
      toast.error("Login failed", {
        description: "An unexpected error occurred. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md">
        <Card className="border-front-gray-200 bg-white shadow-sm">
          <CardHeader className="space-y-1 text-center pb-8">
            <CardTitle className="text-3xl font-bold text-front-gray-900">
              Sign In
            </CardTitle>
            <CardDescription className="text-front-gray-600 text-base">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-front-gray-900 font-semibold">Email</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="you@example.com" 
                          {...field} 
                          disabled={loading}
                          autoComplete="email"
                          autoFocus
                          required
                          className="border-front-gray-300 focus:border-front-gray-900 text-front-gray-900"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-front-gray-900 font-semibold">Password</FormLabel>
                        <Link 
                          to="/forgot-password" 
                          className="text-sm text-front-gray-600 hover:text-front-gray-900 transition-colors"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="Enter your password" 
                          {...field} 
                          disabled={loading}
                          autoComplete="current-password"
                          required
                          className="border-front-gray-300 focus:border-front-gray-900 text-front-gray-900"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex flex-col space-y-4 pt-6">
                <Button 
                  type="submit" 
                  className="w-full bg-front-gray-900 hover:bg-front-gray-800 text-white font-semibold py-3" 
                  disabled={loading || loginAttempts >= 5}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
                {/* Public sign-up disabled: Plant Autopilot is an internal tool. */}
              </CardFooter>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
}
